// KnockoutDraft.js
// Knockout draft screen — web port of KnockoutDraftView.swift.
//
// Differences from the regular Draft (by design, matching iOS):
//   • Candidates come from the SHARED server pool by "{position}_{index}" key —
//     never client-generated, so everyone drafts the same players.
//   • No Rating Reveal / Player Refresh mechanics.
//   • A 90s countdown bar (anchored to the server expiresAt via KnockoutContext)
//     sits below the progress bar; on expiry the session submits a 0 and exits.
//   • On completion the score (chemistry-inclusive teamRating + chemistry points)
//     is submitted as an entry + standing, then we return to the overview.
//     (The polished complete/results screen arrives in the next pass.)

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import wordmark from "../assets/Rosterable.png";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import PlayerCard from "../components/PlayerCard";
import ChemistryTracker from "../components/ChemistryTracker";
import { useAuth } from "../managers/AuthContext";
import { useKnockoutSession } from "../managers/KnockoutContext";
import { SPORT_CONFIG } from "../managers/draftManager";
import { displayPosition } from "../managers/playerService";
import { loadPlayerPool } from "../managers/knockoutService";
import { loadTeamLogos } from "../managers/teamLogoService";
import {
  calculateTeamRatingWithChemistry,
  getChemistryBreakdown,
} from "../managers/teamRatingCalculator";

const SPORT_ICONS = {
  football: footballIcon,
  baseball: baseballIcon,
  basketball: basketballIcon,
  hockey: hockeyIcon,
  soccer: soccerIcon,
};

function formatCountdown(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function KnockoutDraft() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const params = location.state || {};
  const { poolId, sportId, slot, dayNumber, poolTitle } = params;

  // Guard: a draft can only proceed with the navigation params present. A page
  // refresh wipes location.state → bounce to the overview (where re-tapping
  // DRAFT NOW correctly hits the forfeit path). We intentionally do NOT gate on
  // draftSession here: it's set synchronously in a ref inside startSession, but
  // the context *state* can lag one render across the route change, and gating
  // on it would cause a transient bounce.
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!poolId || !sportId || !slot || !dayNumber) {
      navigate("/battles/knockout", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user || !poolId || !sportId || !slot || !dayNumber) return null;

  return (
    <KnockoutDraftGame
      poolId={poolId}
      sportId={sportId}
      slot={slot}
      dayNumber={dayNumber}
      poolTitle={poolTitle}
      navigate={navigate}
    />
  );
}

function KnockoutDraftGame({ poolId, sportId, slot, dayNumber, poolTitle, navigate }) {
  const {
    draftedPlayers,
    currentPositionIndex,
    timeRemaining,
    sessionDidExpire,
    recordPick,
    submitScore,
  } = useKnockoutSession();

  const config = SPORT_CONFIG[sportId];
  const positions = config.positions;
  const totalPositions = positions.length;
  const sport = { id: sportId, hex: config.color };

  const [pool, setPool] = useState(null);
  const [poolError, setPoolError] = useState(false);
  const [isLoadingPool, setIsLoadingPool] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, bumpLogos] = useState(0);

  const submittingRef = useRef(false);

  // Load the shared player pool + team logos once on mount.
  useEffect(() => {
    let cancelled = false;
    loadTeamLogos().then(() => bumpLogos((n) => n + 1)).catch(() => {});
    (async () => {
      try {
        const p = await loadPlayerPool(sportId, slot);
        if (cancelled) return;
        if (!p) setPoolError(true);
        else setPool(p);
      } catch (err) {
        if (!cancelled) {
          console.error("🥊 KnockoutDraft: pool load failed —", err);
          setPoolError(true);
        }
      } finally {
        if (!cancelled) setIsLoadingPool(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sportId, slot]);

  // On timer expiry the context already submitted a 0 — go to the timed-out
  // results screen.
  useEffect(() => {
    if (sessionDidExpire && !submittingRef.current) {
      submittingRef.current = true;
      navigate("/battles/knockout/complete", {
        replace: true,
        state: { sportId, poolId, dayNumber, poolTitle, timedOut: true },
      });
    }
  }, [sessionDidExpire, navigate, sportId, poolId, dayNumber, poolTitle]);

  const currentPosition = positions[currentPositionIndex] ?? "COMPLETE";
  const progressPct = Math.min(1, (draftedPlayers.length + 1) / totalPositions);

  // Candidates for the current slot, by compound "{position}_{occurrenceIndex}"
  // key — the occurrence index = how many times this position appeared earlier.
  const candidates = (() => {
    if (!pool) return [];
    let occurrenceIndex = 0;
    for (let i = 0; i < currentPositionIndex; i++) {
      if (positions[i] === currentPosition) occurrenceIndex++;
    }
    return pool.players[`${currentPosition}_${occurrenceIndex}`] || [];
  })();

  const completeDraft = useCallback(
    async (finalPlayers) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsSubmitting(true);
      const resolvedPositions = positions.map((slotName, i) =>
        displayPosition(slotName, finalPlayers[i])
      );
      const teamRating = calculateTeamRatingWithChemistry(finalPlayers);
      const chemistryPoints = getChemistryBreakdown(finalPlayers).totalChemistryPoints;
      await submitScore({
        teamRating,
        chemistryPoints,
        draftedPlayers: finalPlayers,
        draftedPositions: resolvedPositions,
      });
      // Show the results screen with the drafted team.
      navigate("/battles/knockout/complete", {
        replace: true,
        state: {
          sportId,
          poolId,
          dayNumber,
          poolTitle,
          timedOut: false,
          players: finalPlayers,
          positions: resolvedPositions,
          teamRating,
          chemistryPoints,
        },
      });
    },
    [positions, submitScore, navigate, sportId, poolId, dayNumber, poolTitle]
  );

  function selectPlayer(index) {
    if (selectedIndex !== null || isSubmitting || submittingRef.current) return;
    setSelectedIndex(index);
    const player = candidates[index];
    setTimeout(() => {
      const newDrafted = [...draftedPlayers, player];
      if (newDrafted.length >= totalPositions) {
        recordPick(newDrafted, currentPositionIndex);
        completeDraft(newDrafted);
      } else {
        recordPick(newDrafted, currentPositionIndex + 1);
        setSelectedIndex(null);
      }
    }, 240);
  }

  const timerFraction = Math.min(1, Math.max(0, timeRemaining / 90));
  const timerWarn = timerFraction <= 0.3;
  const timerColor = timerWarn ? "#ff5a5a" : sport.hex;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/battles/knockout")}>
          ‹
        </button>
        <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
        <div style={styles.rosterBtn}>
          <span style={styles.rosterIcon}>☰</span>
          {draftedPlayers.length > 0 && (
            <span style={{ ...styles.rosterBadge, background: sport.hex }}>
              {draftedPlayers.length}
            </span>
          )}
        </div>
      </div>

      {/* Progress */}
      <div style={styles.progressSection}>
        <div style={styles.progressRow}>
          <div style={{ ...styles.sportCircle, background: sport.hex }}>
            <img src={SPORT_ICONS[sportId]} alt="" style={styles.sportCircleIcon} />
          </div>
          <div style={styles.draftingLabel}>
            <span style={styles.draftingText}>DRAFTING</span>
            <span style={{ ...styles.positionLabel, color: sport.hex }}>{currentPosition}</span>
          </div>
          <span style={styles.countLabel}>
            {Math.min(draftedPlayers.length + 1, totalPositions)} OF {totalPositions}
          </span>
        </div>
        <div style={styles.barTrack}>
          <div
            style={{
              ...styles.barFill,
              width: `${progressPct * 100}%`,
              background: `linear-gradient(to right, ${sport.hex}, ${sport.hex}cc)`,
              boxShadow: `0 0 6px ${sport.hex}66`,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* Timer bar (Knockout-only) */}
      <div style={styles.timerRow}>
        <div style={styles.barTrack}>
          <div
            style={{
              ...styles.barFill,
              width: `${timerFraction * 100}%`,
              background: `linear-gradient(to right, ${timerColor}, ${timerColor}cc)`,
              boxShadow: `0 0 6px ${timerColor}66`,
              transition: "width 0.2s linear",
            }}
          />
        </div>
        <span style={{ ...styles.timerText, color: timerColor }}>
          {formatCountdown(timeRemaining)}
        </span>
      </div>

      {/* Cards */}
      <div style={styles.cardsSection}>
        {isLoadingPool ? (
          <div style={styles.stateBox}>
            <span style={styles.stateText}>Loading today's player pool…</span>
          </div>
        ) : poolError ? (
          <div style={styles.stateBox}>
            <span style={styles.stateText}>
              Today's player pool isn't ready yet. Please try again shortly.
            </span>
            <button style={styles.backToOverview} onClick={() => navigate("/battles/knockout")}>
              BACK
            </button>
          </div>
        ) : (
          candidates.map((player, index) => (
            <PlayerCard
              key={`${currentPositionIndex}-${index}`}
              player={player}
              sport={sport}
              position={
                sportId === "football" ? displayPosition(currentPosition, player) : currentPosition
              }
              isSelected={selectedIndex === index}
              showRating={false}
              onTap={() => selectPlayer(index)}
              isDisabled={selectedIndex !== null && selectedIndex !== index}
            />
          ))
        )}
      </div>

      {/* Chemistry tracker */}
      <ChemistryTracker players={draftedPlayers} sport={sport} />
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 24px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 52,
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.8)",
    fontSize: 30,
    lineHeight: 1,
    cursor: "pointer",
    width: 32,
    textAlign: "left",
    padding: 0,
  },
  wordmark: { height: 21, objectFit: "contain" },
  rosterBtn: { position: "relative", width: 32, textAlign: "right" },
  rosterIcon: { color: "rgba(255,255,255,0.8)", fontSize: 20 },
  rosterBadge: {
    position: "absolute",
    top: -8,
    right: -8,
    color: "#000",
    fontSize: 10,
    fontWeight: 800,
    borderRadius: "50%",
    minWidth: 18,
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 2px",
  },
  progressSection: { padding: "8px 0 4px" },
  progressRow: { display: "flex", alignItems: "center", gap: 16, marginBottom: 10 },
  sportCircle: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sportCircleIcon: { width: 26, height: 26, filter: "brightness(0)" },
  draftingLabel: { display: "flex", alignItems: "center", gap: 8, flex: 1 },
  draftingText: { fontSize: 18, fontWeight: 500, color: "rgba(255,255,255,0.7)", letterSpacing: 0.5 },
  positionLabel: { fontSize: 18, fontWeight: 700, letterSpacing: 1 },
  countLabel: { fontSize: 16, fontWeight: 600, color: "rgba(255,255,255,0.6)", letterSpacing: 1 },
  barTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
  },
  barFill: { height: 8, borderRadius: 4 },
  timerRow: { display: "flex", alignItems: "center", gap: 12, padding: "16px 0 8px" },
  timerText: {
    fontSize: 16,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    width: 42,
    textAlign: "right",
  },
  cardsSection: { display: "flex", flexDirection: "column", gap: 10, padding: "8px 0" },
  stateBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
    padding: "40px 20px",
    textAlign: "center",
  },
  stateText: { fontSize: 14, color: "rgba(255,255,255,0.6)" },
  backToOverview: {
    height: 44,
    padding: "0 24px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1,
    cursor: "pointer",
  },
};
